import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, FormGroup, InputGroup, Spinner, TextArea } from '@blueprintjs/core';

import Api from 'src/api';
import Button from 'src/components/controls/forms/redesign/FormButton';
import User from 'src/models/User';
import UserDetails from 'src/models/UserDetails';
import Permissions from 'src/models/Permissions';
import { IPermissionDefinition, IRoleDefinition, IUserRoleAssignment } from 'src/models/RoleDefinition';
import selectors from 'src/selectors';
import Authorization from 'src/util/Authorization';
import ErrorResponse from 'src/models/ErrorResponse';

import './RoleManagementPage.scss';

const emptyRole = (): IRoleDefinition => ({
    id: 0,
    name: '',
    description: '',
    isActive: true,
    isSystem: false,
    permissionIds: [],
});

const ADMIN_ROLE_ID = 1;

const sortUsersByName = (users: User[]): User[] =>
    [...users].sort((left, right) => {
        const lastNameComparison = (left.lastName || '').localeCompare(right.lastName || '', undefined, { sensitivity: 'base' });
        if (lastNameComparison !== 0) {
            return lastNameComparison;
        }
        const firstNameComparison = (left.firstName || '').localeCompare(right.firstName || '', undefined, { sensitivity: 'base' });
        return firstNameComparison !== 0 ? firstNameComparison : (left.username || '').localeCompare(right.username || '');
    });

const isLastAdministratorMessage = (message: string): boolean =>
    !!message && message.toLowerCase().indexOf('last active administrator') >= 0;

const RoleManagementPage: React.FC = () => {
    const currentUser = useSelector(selectors.auth.user) as UserDetails;
    const canManageRoles = Authorization.HasPermission(Permissions.RolesManage, currentUser);
    const canManageUserRoles = Authorization.HasPermission(Permissions.UserRolesManage, currentUser);
    const [roles, setRoles] = useState<IRoleDefinition[]>([]);
    const [permissions, setPermissions] = useState<IPermissionDefinition[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [selectedRole, setSelectedRole] = useState<IRoleDefinition>(emptyRole());
    const [selectedUserId, setSelectedUserId] = useState<number>(0);
    const [selectedUserRoleIds, setSelectedUserRoleIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [assignmentMessage, setAssignmentMessage] = useState('');

    const loadRoles = async () => {
        const result = (await Api.RoleManagement.getRoles()) as unknown as IRoleDefinition[];
        if (Array.isArray(result)) {
            setRoles(result);
            if (selectedRole.id) {
                setSelectedRole(result.find((role) => role.id === selectedRole.id) || emptyRole());
            }
        }
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            const [roleResult, permissionResult, userResult] = await Promise.all([
                Api.RoleManagement.getRoles(),
                canManageRoles ? Api.RoleManagement.getPermissions() : Promise.resolve([]),
                canManageUserRoles ? Api.User.getAll(currentUser.jwt) : Promise.resolve([]),
            ]);
            setRoles(Array.isArray(roleResult) ? (roleResult as IRoleDefinition[]) : []);
            setPermissions(Array.isArray(permissionResult) ? (permissionResult as IPermissionDefinition[]) : []);
            setUsers(Array.isArray(userResult) ? sortUsersByName(userResult as User[]) : []);
            setLoading(false);
        };
        load();
    }, [canManageRoles, canManageUserRoles, currentUser.jwt]);

    const selectRole = (role: IRoleDefinition) => {
        setSelectedRole({ ...role, permissionIds: [...(role.permissionIds || [])] });
        setMessage('');
    };

    const togglePermission = (permissionId: number) => {
        setSelectedRole((role) => ({
            ...role,
            permissionIds: role.permissionIds.includes(permissionId)
                ? role.permissionIds.filter((id) => id !== permissionId)
                : [...role.permissionIds, permissionId],
        }));
    };

    const saveRole = async () => {
        if (!selectedRole.name.trim()) {
            setMessage('Role name is required.');
            return;
        }
        setSaving(true);
        const result = selectedRole.id ? await Api.RoleManagement.updateRole(selectedRole) : await Api.RoleManagement.createRole(selectedRole);
        setSaving(false);
        const error = result as ErrorResponse;
        if (error && error.isError) {
            setMessage(error.message || 'Unable to save role.');
            return;
        }
        setMessage('Role saved. Users will receive updated access when a new JWT is issued.');
        await loadRoles();
    };

    const selectUser = async (userId: number) => {
        setSelectedUserId(userId);
        setMessage('');
        setAssignmentMessage('');
        if (!userId) {
            setSelectedUserRoleIds([]);
            return;
        }
        const result = (await Api.RoleManagement.getUserRoles(userId)) as unknown as IUserRoleAssignment[];
        setSelectedUserRoleIds(Array.isArray(result) ? result.map((assignment) => assignment.roleId) : []);
    };

    const toggleUserRole = (roleId: number) => {
        setAssignmentMessage('');
        setSelectedUserRoleIds((roleIds) => (roleIds.includes(roleId) ? roleIds.filter((id) => id !== roleId) : [...roleIds, roleId]));
    };

    const saveUserRoles = async () => {
        if (!selectedUserId) {
            setAssignmentMessage('Select a user before saving role assignments.');
            return;
        }
        if (selectedUserRoleIds.length === 0) {
            setAssignmentMessage('At least one role is required. No changes were saved.');
            return;
        }
        setSaving(true);
        const result = await Api.RoleManagement.setUserRoles(selectedUserId, selectedUserRoleIds);
        setSaving(false);
        const error = result as ErrorResponse;
        setAssignmentMessage(
            error && error.isError
                ? error.message || 'Unable to save user roles.'
                : 'User roles saved. The user must sign in again to receive updated access.'
        );
    };

    const focusUserAssignments = () => {
        const assignments = document.getElementById('user-role-assignments');
        if (assignments) {
            assignments.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    if (loading) {
        return (
            <div className="role-management-loading">
                <Spinner size={24} />
                <span>Loading role management...</span>
            </div>
        );
    }

    return (
        <div className="role-management-page">
            <header className="role-management-header">
                <div>
                    <h2>Role Management</h2>
                    <p>
                        {canManageRoles && canManageUserRoles
                            ? 'Define access by role, then assign one or more roles to each user.'
                            : canManageRoles
                            ? 'Define the permissions included in each role.'
                            : 'Assign one or more existing roles to each user.'}
                    </p>
                </div>
                {canManageRoles && <Button text="New Role" intent="primary" disabled={!selectedRole.id} onClick={() => selectRole(emptyRole())} />}
            </header>

            {message && (
                <div role="status" className="role-management-status">
                    {message}
                    {isLastAdministratorMessage(message) && (
                        <Button text="Assign Another Administrator" onClick={focusUserAssignments} />
                    )}
                </div>
            )}

            {canManageRoles && (
                <div className="role-management-grid">
                    <Card className="role-list-panel" elevation={1}>
                    <div className="role-management-section-heading">
                        <h3>Roles</h3>
                        <span>{roles.length}</span>
                    </div>
                    <ul className="role-list">
                        {roles.map((role) => (
                            <li key={role.id}>
                                <button type="button" className={selectedRole.id === role.id ? 'selected' : ''} onClick={() => selectRole(role)}>
                                    <span>{role.name}</span>
                                    {!role.isActive && <small>Inactive</small>}
                                </button>
                            </li>
                        ))}
                    </ul>
                    </Card>

                    <Card className="role-editor-panel" elevation={1}>
                    <div className="role-management-section-heading">
                        <div>
                            <h3>{selectedRole.id ? `Edit ${selectedRole.name}` : 'New Role'}</h3>
                            <p>Changes affect users after their next sign-in.</p>
                        </div>
                    </div>

                    <div className="role-fields">
                        <FormGroup label="Name" labelFor="role-name">
                            <InputGroup
                                id="role-name"
                                value={selectedRole.name}
                                maxLength={100}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setSelectedRole({ ...selectedRole, name: event.target.value })
                                }
                            />
                        </FormGroup>
                        <FormGroup label="Description" labelFor="role-description">
                            <TextArea
                                id="role-description"
                                fill={true}
                                value={selectedRole.description || ''}
                                maxLength={500}
                                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                                    setSelectedRole({ ...selectedRole, description: event.target.value })
                                }
                            />
                        </FormGroup>
                    </div>

                    <label className="role-active-toggle">
                        <input
                            type="checkbox"
                            checked={selectedRole.isActive}
                            disabled={selectedRole.id === ADMIN_ROLE_ID}
                            title={selectedRole.id === ADMIN_ROLE_ID ? 'The Admin role must remain active.' : undefined}
                            onChange={(event) => setSelectedRole({ ...selectedRole, isActive: event.target.checked })}
                        />{' '}
                        Active
                        {selectedRole.id === ADMIN_ROLE_ID && <small>The Admin role must remain active.</small>}
                    </label>

                    <div className="permission-heading">
                        <h4>Permissions</h4>
                        <span>Select the capabilities included in this role.</span>
                    </div>
                    <div className="permission-grid">
                        {permissions
                            .filter((permission) => permission.isActive)
                            .map((permission) => (
                                <label key={permission.id} className="permission-option">
                                    <input
                                        type="checkbox"
                                        checked={selectedRole.permissionIds.includes(permission.id)}
                                        onChange={() => togglePermission(permission.id)}
                                    />
                                    <span>
                                        <strong>{permission.name}</strong>
                                        <small>{permission.key}</small>
                                    </span>
                                </label>
                            ))}
                    </div>
                    <div className="role-management-actions">
                        <Button
                            text={selectedRole.id ? 'Save Changes' : 'Create Role'}
                            intent="primary"
                            disabled={saving}
                            loading={saving}
                            onClick={saveRole}
                        />
                    </div>
                    </Card>
                </div>
            )}

            {canManageUserRoles && (
                <Card id="user-role-assignments" className="user-role-panel" elevation={1}>
                    <div className="role-management-section-heading">
                        <div>
                            <h3>User Role Assignments</h3>
                            <p>Users receive the combined permissions from every assigned role.</p>
                        </div>
                    </div>
                    <FormGroup label="User" labelFor="role-user">
                        <div className="user-select-wrapper">
                            <select id="role-user" value={selectedUserId} onChange={(event) => selectUser(parseInt(event.target.value, 10) || 0)}>
                                <option value={0}>Select a user</option>
                                {users.map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.lastName}, {user.firstName} ({user.username})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </FormGroup>
                    {selectedUserId > 0 && (
                        <div className="user-role-editor">
                            <div className="user-role-options">
                                {roles
                                    .filter((role) => role.isActive)
                                    .map((role) => (
                                        <label key={role.id} className="user-role-option">
                                            <input
                                                type="checkbox"
                                                checked={selectedUserRoleIds.includes(role.id)}
                                                onChange={() => toggleUserRole(role.id)}
                                            />{' '}
                                            {role.name}
                                        </label>
                                    ))}
                            </div>
                            {assignmentMessage && (
                                <div role="status" className="user-role-assignment-status">
                                    {assignmentMessage}
                                    {isLastAdministratorMessage(assignmentMessage) && (
                                        <Button text="Assign Another Administrator" onClick={focusUserAssignments} />
                                    )}
                                </div>
                            )}
                            <div className="role-management-actions">
                                <Button text="Save User Roles" intent="primary" disabled={saving} loading={saving} onClick={saveUserRoles} />
                            </div>
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
};

export default RoleManagementPage;



// WEBPACK FOOTER //
// ./src/components/admin/sections/RoleManagementPage.tsx